import { useEffect } from 'react';

import { useEcho } from '@/contexts/echo/echo-provider';

/**
 * Subscribe to a Laravel Echo private channel event.
 * Automatically handles subscribe/unsubscribe lifecycle.
 *
 * @param channelName - Private channel name without the "private-" prefix (e.g. "user.1")
 * @param eventName   - Broadcast event name, with leading dot for custom broadcastAs names (e.g. ".community.changed")
 * @param callback    - Handler called with the event payload
 * @param deps        - Extra deps that should re-trigger the subscription
 */
export function useEchoPrivateEvent(
  channelName: string,
  eventName: string,
  callback: (data: any) => void,
  deps: any[] = []
): void {
  const { echo } = useEcho();

  useEffect(() => {
    if (!echo) {
      console.log(`[useEchoPrivateEvent] No Echo instance — skipping ${channelName}:${eventName}`);
      return;
    }
    if (!channelName) return;

    console.log(`[useEchoPrivateEvent] Subscribing to private channel "${channelName}" → "${eventName}"`);

    const channel = echo.private(channelName);

    channel.listen(eventName, callback);

    channel.error((err: any) => {
      console.error(`[useEchoPrivateEvent] Channel error on "${channelName}":`, err);
    });

    return () => {
      console.log(`[useEchoPrivateEvent] Leaving channel "${channelName}"`);
      echo.leave(channelName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [echo, channelName, eventName, ...deps]);
}
